import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Columns3 } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import {
  fetchAccountantDonationPayments,
  verifyAccountantDonationPayment,
  verifyAccountantDonationPaymentsBulk,
} from "../api/donationPaymentsApi";
import { GlobalDirectoryPaginationSection as VolunteerPagination } from "../../../global/directory";
import { getCsrfToken } from "../../../public/api/authApi";
import { GlobalModal } from "../../../global/GlobalModal";
import GlobalErrorFlashModal from "../../../global/GlobalErrorFlashModal";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { GlobalButton, GlobalSelectField, GlobalTextField } from "../../../global/forms/GlobalFormFields";
import GlobalBadge from "../../../global/GlobalBadge";
import { GlobalTableActionButton } from "../../../global/GlobalTableActionButton";
import PaymentProofViewButton from "../../payment/PaymentProofViewButton";
import "../styles/VolunteerTable.css";
import "../styles/participantsList.css";
import "../styles/tournamentAdminApprovals.css";
import "../styles/tournamentPaymentsPage.css";
import "../styles/MembershipApplications.css";
import { formatDonationBulkPaymentSkipSuffix } from "../utils/donationBulkSkipMessage";

const PAGE_SIZE = 50;
const COL_KEY = "girinestham.accountant.donationPayments.columns.v2";
const COLUMN_DEFS = [
  { id: "donor_code", label: "Donor code", defaultVisible: true, sortable: true },
  { id: "donor_name", label: "Donor name", defaultVisible: true, sortable: true },
  { id: "donor_contact", label: "Contact", defaultVisible: false, sortable: true },
  { id: "donation_receipt_no", label: "Donation ID", defaultVisible: true, sortable: true },
  { id: "donation_date", label: "Donation date", defaultVisible: false, sortable: true },
  { id: "donation_verified", label: "Donation verified", defaultVisible: true, sortable: true },
  { id: "payment_id", label: "Payment ID", defaultVisible: false, sortable: true },
  { id: "payment_type", label: "Payment type", defaultVisible: true, sortable: true },
  { id: "transaction_id", label: "Transaction ID", defaultVisible: true, sortable: true },
  { id: "amount", label: "Amount", defaultVisible: true, sortable: true },
  { id: "phonepe_order_id", label: "PhonePe order ID", defaultVisible: true, sortable: true },
  { id: "phonepe_state", label: "PhonePe state", defaultVisible: false, sortable: true },
  { id: "phonepe_currency", label: "PhonePe currency", defaultVisible: false, sortable: true },
  { id: "phonepe_expire_at", label: "PhonePe expire at", defaultVisible: false, sortable: true },
  { id: "phonepe_tpap_link", label: "PhonePe TPAP link", defaultVisible: false, sortable: true },
  { id: "phonepe_payment_mode", label: "PhonePe payment mode", defaultVisible: false, sortable: true },
  { id: "phonepe_timestamp", label: "PhonePe timestamp", defaultVisible: false, sortable: true },
  { id: "phonepe_fee_amount", label: "PhonePe fee", defaultVisible: false, sortable: true },
  { id: "phonepe_payable_amount", label: "PhonePe payable", defaultVisible: false, sortable: true },
  { id: "phonepe_split_instruments", label: "PhonePe split instruments", defaultVisible: false, sortable: false },
  { id: "payment_status", label: "Payment status", defaultVisible: true, sortable: true },
  { id: "payment_verified", label: "Payment verified", defaultVisible: true, sortable: true },
  { id: "collected_by_id", label: "Collected by ID", defaultVisible: false, sortable: true },
  { id: "collected_by_name", label: "Collected by", defaultVisible: true, sortable: true },
  { id: "proof", label: "Proof", defaultVisible: false, sortable: true },
  { id: "purpose_id", label: "Purpose ID", defaultVisible: false, sortable: true },
  { id: "purpose_name", label: "Purpose", defaultVisible: true, sortable: true },
  { id: "payment_remarks", label: "Remarks", defaultVisible: false, sortable: true },
  { id: "student_id", label: "Student ID", defaultVisible: false, sortable: true },
  { id: "is_active", label: "Record status ID", defaultVisible: false, sortable: true },
  { id: "created_at", label: "Created at", defaultVisible: true, sortable: true },
];

function loadVis() {
  let saved = null;
  try {
    const raw = localStorage.getItem(COL_KEY);
    if (raw) saved = JSON.parse(raw);
  } catch {}
  const initial = {};
  for (const c of COLUMN_DEFS) initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
  return initial;
}
const isTruthyBool = (v) => v === true || v === 1 || v === "1";
function formatDateCell(value) {
  if (value == null || value === "") return "—";
  try {
    const raw = String(value).trim();
    let d = parseISO(raw);
    if (!isValid(d) && /^\d{4}-\d{2}-\d{2}/.test(raw)) d = new Date(raw.replace(" ", "T"));
    if (!isValid(d)) d = new Date(raw);
    return isValid(d) ? format(d, "PP") : "—";
  } catch {
    return "—";
  }
}
function formatMoney(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `₹${n.toFixed(2)}` : "—";
}
function formatUnixMs(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  try {
    const d = new Date(n);
    return isValid(d) ? format(d, "PPpp") : "—";
  } catch {
    return "—";
  }
}
function formatBoolFlag(value, trueLabel = "Yes", falseLabel = "No") {
  return isTruthyBool(value) ? trueLabel : falseLabel;
}
function cellText(row, colId) {
  if (colId === "donation_date" || colId === "created_at") return formatDateCell(row[colId]);
  if (colId === "amount" || colId === "phonepe_fee_amount" || colId === "phonepe_payable_amount") {
    return formatMoney(row[colId]);
  }
  if (colId === "phonepe_timestamp" || colId === "phonepe_expire_at") return formatUnixMs(row[colId]);
  if (colId === "payment_verified" || colId === "donation_verified") {
    return isTruthyBool(row[colId]) ? "Verified" : "Not verified";
  }
  if (colId === "payment_status") return formatBoolFlag(row[colId], "Completed", "Pending");
  if (colId === "payment_type") {
    const t = String(row[colId] || "").toLowerCase();
    if (t === "online") return "Online";
    if (t === "offline") return "Offline";
    return row[colId] ?? "—";
  }
  if (colId === "phonepe_split_instruments") {
    const raw = row[colId];
    if (raw == null || raw === "") return "—";
    if (typeof raw === "object") {
      try {
        return JSON.stringify(raw);
      } catch {
        return "—";
      }
    }
    return String(raw);
  }
  return row[colId] ?? "—";
}

export default function DonationPaymentsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: PAGE_SIZE, totalPages: 1 });
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [colVis, setColVis] = useState(() => loadVis());
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  const headerCheckboxRef = useRef(null);
  const [detail, setDetail] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState(() => new Set());
  const [draft, setDraft] = useState({ donor_search: "", column: "", value: "", date_from: "", date_to: "" });
  const [applied, setApplied] = useState({ donor_search: "", column: "", value: "", date_from: "", date_to: "" });

  useEffect(() => { const t = setTimeout(() => setApplied({ ...draft }), 400); return () => clearTimeout(t); }, [draft]);
  useEffect(() => { setPage(1); setSelectedPaymentIds(new Set()); }, [applied.donor_search, applied.column, applied.value, applied.date_from, applied.date_to]);
  useEffect(() => { const onDoc = (e) => { if (!pickerRef.current?.contains(e.target)) setPickerOpen(false); }; if (pickerOpen) document.addEventListener("mousedown", onDoc); return () => document.removeEventListener("mousedown", onDoc); }, [pickerOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const queryFilters = {
        donor_search: (applied.donor_search || "").trim() || undefined,
        date_from: applied.date_from || undefined,
        date_to: applied.date_to || undefined,
        page: String(page),
        limit: String(PAGE_SIZE),
        sort_column: sortColumn,
        sort_order: sortOrder,
      };
      if (applied.column && applied.value) {
        if (applied.column === "payment_verified") queryFilters.payment_verified = applied.value === "Verified" ? "1" : "0";
        else if (applied.column === "donation_verified") queryFilters.donation_verified = applied.value === "Verified" ? "1" : "0";
        else if (applied.column === "donor_name" || applied.column === "donor_code") queryFilters.donor_search = String(applied.value || "").trim() || undefined;
      }
      const res = await fetchAccountantDonationPayments(queryFilters);
      setRows(Array.isArray(res?.data) ? res.data : []);
      const m = res?.meta || {};
      setMeta({ total: Number(m.total) || 0, page: Number(m.page) || page, limit: Number(m.limit) || PAGE_SIZE, totalPages: Math.max(Number(m.totalPages) || 1, 1) });
    } catch (e) {
      setErrorMsg(e?.message || "Could not load data.");
      setRows([]);
      setMeta({ total: 0, page: 1, limit: PAGE_SIZE, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [applied, page, sortColumn, sortOrder]);
  useEffect(() => { load(); }, [load]);

  const columnFilterOptions = useMemo(() => COLUMN_DEFS.map((c) => ({ value: c.id, label: c.label })), []);
  const valueFilterOptions = useMemo(() => {
    if (!draft.column) return [];
    if (draft.column === "payment_verified" || draft.column === "donation_verified") return [{ value: "Verified", label: "Verified" }, { value: "Not verified", label: "Not verified" }];
    const seen = new Set();
    const out = [];
    for (const row of rows) {
      const raw = cellText(row, draft.column);
      if (raw == null || raw === "") continue;
      const label = String(raw);
      if (seen.has(label)) continue;
      seen.add(label);
      out.push({ value: label, label });
    }
    return out.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }));
  }, [draft.column, rows]);

  const filteredRows = useMemo(() => {
    const col = applied.column;
    const val = String(applied.value || "").trim();
    if (!col || !val) return rows;
    const match = val.toLowerCase();
    return rows.filter((row) => String(cellText(row, col)).trim().toLowerCase() === match);
  }, [rows, applied.column, applied.value]);

  const visibleCols = useMemo(() => COLUMN_DEFS.filter((c) => colVis[c.id]), [colVis]);
  const selectablePaymentIdsOnPage = useMemo(() => filteredRows.filter((r) => r.payment_id != null).map((r) => r.payment_id), [filteredRows]);
  const allPagePaymentsSelected = selectablePaymentIdsOnPage.length > 0 && selectablePaymentIdsOnPage.every((id) => selectedPaymentIds.has(id));
  const somePagePaymentsSelected = selectablePaymentIdsOnPage.some((id) => selectedPaymentIds.has(id));
  useEffect(() => { const el = headerCheckboxRef.current; if (el) el.indeterminate = !allPagePaymentsSelected && somePagePaymentsSelected; }, [allPagePaymentsSelected, somePagePaymentsSelected]);

  const toggleSort = (c) => {
    if (!c.sortable) return;
    setPage(1);
    if (sortColumn === c.id) setSortOrder((o) => (o === "ASC" ? "DESC" : "ASC"));
    else { setSortColumn(c.id); setSortOrder("ASC"); }
  };
  const toggleAllPaymentsOnPage = () => {
    setSelectedPaymentIds((prev) => {
      const next = new Set(prev);
      const allOn = selectablePaymentIdsOnPage.length > 0 && selectablePaymentIdsOnPage.every((id) => next.has(id));
      if (allOn) selectablePaymentIdsOnPage.forEach((id) => next.delete(id));
      else selectablePaymentIdsOnPage.forEach((id) => next.add(id));
      return next;
    });
  };
  const toggleCol = (id) => {
    setColVis((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      if (COLUMN_DEFS.filter((c) => next[c.id]).length === 0) return prev;
      try { localStorage.setItem(COL_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const csrf = async () => {
    let t = localStorage.getItem("csrf-token");
    if (!t) { t = await getCsrfToken(); localStorage.setItem("csrf-token", t); }
    return t;
  };
  const handlePaymentVerification = async (row, wantVerified) => {
    const pid = row?.payment_id;
    if (pid == null) return;
    setVerifyBusy(pid);
    setErrorMsg("");
    try {
      await verifyAccountantDonationPayment(pid, await csrf(), wantVerified);
      setSuccessMsg(wantVerified ? "Payment marked verified." : "Payment marked not verified.");
      setSelectedPaymentIds((prev) => { const next = new Set(prev); next.delete(pid); return next; });
      await load();
      setDetail((d) => (d && d.payment_id === pid ? { ...d, payment_verified: wantVerified } : d));
    } catch (e) {
      setErrorMsg(e?.message || "Could not update payment verification.");
    } finally {
      setVerifyBusy(null);
    }
  };
  const handleBulkPaymentVerification = async (wantVerified) => {
    const ids = Array.from(selectedPaymentIds).filter((id) => Number.isFinite(id) && id > 0);
    if (ids.length === 0) return;
    setBulkBusy(true);
    setErrorMsg("");
    try {
      const res = await verifyAccountantDonationPaymentsBulk(ids, await csrf(), wantVerified);
      const v = res?.data?.processed ?? 0;
      const s = res?.data?.skipped ?? 0;
      const skipDetail = formatDonationBulkPaymentSkipSuffix(res?.data?.skip_reasons);
      setSuccessMsg(
        wantVerified
          ? `Applied verified to ${v} payment(s)${s ? ` · ${s} skipped.` : "."}${skipDetail}`
          : `Marked ${v} payment(s) not verified${s ? ` · ${s} skipped.` : "."}${skipDetail}`,
      );
      setSelectedPaymentIds(new Set());
      await load();
    } catch (e) {
      setErrorMsg(e?.message || "Bulk update failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="vpart-page accountant-tpay-page">
      <GlobalErrorFlashModal message={errorMsg} onDismiss={() => setErrorMsg("")} />
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <header className="vpart-hero">
        <p className="vpart-hero__eyebrow">Accountant</p>
        <h1 className="vpart-hero__title">Donations payments</h1>
        <p className="vpart-hero__sub">Review donation payments. Only payments with purpose containing "donation" are shown.</p>
      </header>
      <section className="vpart-filters-section dpay-directory-filters" aria-label="Donation payment filters">
        <div className="td-approvals-hscroll">
          <div className="vpart-filters-row" style={{ minWidth: "min(100%, 58rem)" }}>
            <div className="vpart-filters-row__grow">
              <GlobalTextField label="Search donor" placeholder="Name, code, phone" value={draft.donor_search} onChange={(v) => setDraft((f) => ({ ...f, donor_search: v }))} />
            </div>
            <div className="vpart-filters-row__field" style={{ width: "11.5rem" }}>
              <GlobalSelectField label="Column" value={draft.column} onChange={(v) => setDraft((f) => ({ ...f, column: v, value: "" }))} placeholderOption="Select" options={columnFilterOptions} />
            </div>
            <div className="vpart-filters-row__field" style={{ width: "11.5rem" }}>
              <GlobalSelectField label="Value" value={draft.value} onChange={(v) => setDraft((f) => ({ ...f, value: v }))} placeholderOption="Any" options={valueFilterOptions} />
            </div>
            <div className="vpart-filters-row__field" style={{ width: "9.5rem" }}>
              <GlobalTextField label="Created from" type="date" value={draft.date_from} onChange={(v) => setDraft((f) => ({ ...f, date_from: v }))} />
            </div>
            <div className="vpart-filters-row__field" style={{ width: "9.5rem" }}>
              <GlobalTextField label="Created to" type="date" value={draft.date_to} onChange={(v) => setDraft((f) => ({ ...f, date_to: v }))} />
            </div>
            <div className="vpart-filters-row__field" style={{ width: "10rem" }}>
              <GlobalButton type="button" size="sm" variant="ghost" onClick={() => setDraft((f) => ({ ...f, column: "", value: "" }))}>Clear filter</GlobalButton>
            </div>
          </div>
        </div>
      </section>
      <div className="bulk-actions-bar">
        <div className="bulk-actions-info">
          <span className="selection-count">Selection</span>
          <span className="selection-hint">Mark selected donation payments as verified/not verified.</span>
        </div>
        <div className="bulk-actions-buttons">
          <span className="selection-count" aria-live="polite">{selectedPaymentIds.size} selected</span>
          <GlobalButton type="button" variant="primary" size="sm" disabled={selectedPaymentIds.size === 0 || bulkBusy} onClick={() => handleBulkPaymentVerification(true)}>
            {bulkBusy ? "Working…" : "Mark selected verified"}
          </GlobalButton>
          <GlobalButton type="button" variant="ghost" size="sm" disabled={selectedPaymentIds.size === 0 || bulkBusy} onClick={() => handleBulkPaymentVerification(false)}>
            {bulkBusy ? "Working…" : "Mark selected not verified"}
          </GlobalButton>
        </div>
      </div>
      <div className="td-approvals-column-toolbar">
        <div className="vpart-column-row" style={{ marginBottom: 0, justifyContent: "flex-start", minWidth: "min(100%, 20rem)" }}>
          <div className="vt-column-picker" ref={pickerRef}>
            <GlobalButton type="button" size="sm" variant="ghost" onClick={() => setPickerOpen((o) => !o)} aria-expanded={pickerOpen} aria-controls="dpay-cols-panel">
              <Columns3 size={16} aria-hidden />
              <span style={{ marginLeft: "0.35rem" }}>Columns</span>
            </GlobalButton>
            {pickerOpen ? (
              <div id="dpay-cols-panel" className="vt-column-picker__panel" role="group" aria-label="Columns">
                <p className="vt-column-picker__title">Show columns</p>
                <ul className="vt-column-picker__list">
                  {COLUMN_DEFS.map((c) => (
                    <li key={c.id}>
                      <label className="vt-column-picker__row">
                        <input type="checkbox" checked={!!colVis[c.id]} onChange={() => toggleCol(c.id)} />
                        <span>{c.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {loading ? (
        <p className="ub-loading" style={{ marginTop: "0.75rem" }}>Loading…</p>
      ) : (
        <>
          <div className="vt-table-wrap td-approvals-table-scroll">
            <table className="vt-table" aria-label="Donation payments">
              <thead>
                <tr>
                  <th scope="col" style={{ width: "2.5rem", textAlign: "center" }}>
                    <input ref={headerCheckboxRef} type="checkbox" checked={allPagePaymentsSelected} onChange={toggleAllPaymentsOnPage} disabled={filteredRows.length === 0 || selectablePaymentIdsOnPage.length === 0} />
                  </th>
                  {visibleCols.map((c) => (
                    <th key={c.id} scope="col">
                      {c.sortable ? <button type="button" className="vpart-sort-btn" onClick={() => toggleSort(c)}>{c.label}{sortColumn === c.id ? <span className="vpart-sort-ind" aria-hidden>{sortOrder === "ASC" ? "▲" : "▼"}</span> : null}</button> : c.label}
                    </th>
                  ))}
                  <th scope="col" className="td-approvals-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr><td colSpan={visibleCols.length + 2} style={{ textAlign: "center", padding: "1.5rem" }}>No rows match your filters.</td></tr>
                ) : filteredRows.map((row) => (
                  <tr key={row.entry_key || `${row.donation_id}-${row.payment_id}`}>
                    <td style={{ textAlign: "center" }}>
                      <input type="checkbox" checked={row.payment_id != null && selectedPaymentIds.has(row.payment_id)} disabled={row.payment_id == null} onChange={() => {
                        if (row.payment_id == null) return;
                        setSelectedPaymentIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(row.payment_id)) next.delete(row.payment_id);
                          else next.add(row.payment_id);
                          return next;
                        });
                      }} />
                    </td>
                    {visibleCols.map((c) => (
                      <td key={c.id}>
                        {c.id === "payment_verified" || c.id === "donation_verified" ? (
                          <GlobalBadge active={isTruthyBool(row[c.id])} activeText="Verified" inactiveText="Not verified" />
                        ) : c.id === "payment_status" ? (
                          <GlobalBadge active={isTruthyBool(row[c.id])} activeText="Completed" inactiveText="Pending" />
                        ) : c.id === "donor_name" ? (
                          <span style={{ fontWeight: 600 }}>{cellText(row, c.id)}</span>
                        ) : c.id === "proof" ? (
                          <PaymentProofViewButton proofUrl={row.proof || row.payment_proof} />
                        ) : (
                          cellText(row, c.id)
                        )}
                      </td>
                    ))}
                    <td className="td-approvals-actions">
                      <div className="td-approvals-actions-inner tpay-actions">
                        <GlobalTableActionButton action="view" variant="ghost" onClick={() => setDetail(row)} />
                        {row.payment_id != null ? (
                          <>
                            <GlobalTableActionButton
                              action="verify"
                              loading={verifyBusy === row.payment_id}
                              onClick={() => handlePaymentVerification(row, true)}
                            />
                            <GlobalTableActionButton
                              action="unverify"
                              variant="ghost"
                              disabled={verifyBusy === row.payment_id}
                              onClick={() => handlePaymentVerification(row, false)}
                            />
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <VolunteerPagination page={meta.page} totalPages={meta.totalPages} total={meta.total} pageSize={meta.limit} onPageChange={setPage} label="Donation payments pages" />
        </>
      )}
      <GlobalModal open={detail != null} onClose={() => setDetail(null)} title="Payment detail" description={detail ? `${detail.donor_name} — ${detail.donation_receipt_no}` : undefined} size="lg">
        {detail ? (
          <div className="vpart-reg-modal__scroll">
            <article className="vpart-reg-card">
              <h3 className="vpart-reg-card__title">Donor</h3>
              <div className="vpart-reg-fields">
                <div className="vpart-reg-field"><span className="vpart-reg-field__label">Code</span><span className="vpart-reg-field__value">{detail.donor_code ?? "—"}</span></div>
                <div className="vpart-reg-field"><span className="vpart-reg-field__label">Name</span><span className="vpart-reg-field__value">{detail.donor_name ?? "—"}</span></div>
                <div className="vpart-reg-field"><span className="vpart-reg-field__label">Contact</span><span className="vpart-reg-field__value">{detail.donor_contact ?? "—"}</span></div>
              </div>
            </article>
            <article className="vpart-reg-card vpart-reg-card--payment">
              <h3 className="vpart-reg-card__title">Payment</h3>
              <div className="vpart-reg-fields">
                {COLUMN_DEFS.filter((c) => !["donor_code", "donor_name", "donor_contact", "donation_receipt_no", "donation_date", "donation_verified"].includes(c.id)).map((c) => (
                  <div key={c.id} className="vpart-reg-field">
                    <span className="vpart-reg-field__label">{c.label}</span>
                    <span className="vpart-reg-field__value">
                      {c.id === "proof" ? (
                        <PaymentProofViewButton proofUrl={detail.proof || detail.payment_proof} />
                      ) : (
                        cellText(detail, c.id)
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </article>
          </div>
        ) : null}
      </GlobalModal>
    </div>
  );
}

